import axios from 'axios'
import { getApiBase } from '../../config'
import { getModel } from '../../data/Model'

let api = null

// Lazy initialization of axios instance
const getApi = () => {
	if (!api) {
		api = axios.create({
			baseURL: getApiBase(),
			headers: {
				'Content-Type': 'application/json',
				Accept: 'application/json',
			},
		})

		// Add auth token to all requests
		api.interceptors.request.use((config) => {
			const authToken = getModel().auth_token
			if (authToken) {
				config.headers['X-Copyright-Agent-Auth-Token'] = authToken
			}
			return config
		})

		// Handle response errors
		api.interceptors.response.use(
			(response) => response,
			(error) => {
				if (error.response && error.response.status === 401) {
					// Handle authentication errors
					window.location.href = '/login'
				}
				return Promise.reject(error)
			},
		)
	}
	return api
}

/**
 * Update violation type
 * @param {Object} params - Parameters object
 * @param {number} params.violationId - The violation ID
 * @param {string} params.violationType - The violation type
 * @returns {Promise} - Resolves when the violation type is updated
 */
export const updateViolationType = async ({ violationId, violationType }) => {
	await getApi().patch(`/violations/${violationId}/violationType`, {
		violationType,
	})
}

/**
 * Update internal comment for a violation
 * @param {number} violationId - The violation ID
 * @param {string} internalComment - The internal comment text
 * @returns {Promise} - Resolves when the comment is updated
 */
export const updateInternalComment = async (violationId, internalComment) => {
	await getApi().patch(`/violations/${violationId}/internalComment`, {
		internalComment,
	})
}

/**
 * Mark violation as ready for export
 * @param {number} violationId - The violation ID
 * @returns {Promise} - Resolves when the violation is marked
 */
export const markViolationForExport = async (violationId) => {
	await getApi().patch(`/violations/${violationId}/state`, {
		state: 'ready_for_export',
	})
}

/**
 * Mark violation as no contact information available
 * @param {number} violationId - The violation ID
 * @returns {Promise} - Resolves when the violation is marked
 */
export const markNoContactInformation = async (violationId) => {
	await getApi().patch(`/violations/${violationId}/state`, {
		state: 'rejected',
		is_violating: false,
		non_violating_reason: 'NoContactInformation',
	})
}

/**
 * Update first date of publication for a violation
 * @param {number} violationId - The violation ID
 * @param {string} firstDateOfPublication - The date value
 * @returns {Promise} - Resolves when updated
 */
export const updateFirstDateOfPublication = async (violationId, firstDateOfPublication) => {
	await getApi().patch(`/violations/${violationId}/firstDateOfPublication`, {
		firstDateOfPublication,
	})
}

/**
 * Update wayback URL for a violation
 * @param {number} violationId - The violation ID
 * @param {string} waybackUrl - The wayback URL
 * @returns {Promise} - Resolves when updated
 */
export const updateWaybackUrl = async (violationId, waybackUrl) => {
	await getApi().patch(`/violations/${violationId}/waybackUrl`, {
		waybackUrl,
	})
}

/**
 * Update market for a violation
 * @param {number} violationId - The violation ID
 * @param {string} market - The market code (e.g. 'US', 'DK')
 * @returns {Promise} - Resolves when updated
 */
export const updateViolationMarket = async (violationId, market) => {
	await getApi().patch(`/violations/${violationId}/market`, {
		market,
	})
}

/**
 * Create new organizations from split operation
 * @param {Array} organizations - Array of organization objects to create
 * @returns {Promise} - Resolves when organizations are created
 */
export const createOrganizations = async (organizations) => {
	await getApi().post('/organizations', organizations)
}

/**
 * Look up an existing organization by company number
 * @param {string} companyNumber - The company number to search for
 * @returns {Promise<Object|null>} - The organization if found, null otherwise
 */
export const lookupOrganizationByCompanyNumber = async (companyNumber) => {
	const response = await getApi().get('/organizations/by-company-number', {
		params: { companyNumber },
	})
	return response.data
}
